/**
 * The sample × field table of a run: one row per sample, the field columns shown (plan fields, the AI-found fields
 * the run marked relevant or the reviewer added, the reviewer's own columns) and each cell's value. The Samples
 * Testing tab and the working paper (PDF / Excel) are both built from these functions.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface PlanField {
  id?: string;
  label?: string | null;
  field_key?: string | null;
  _liveLabel?: string | null;
  [key: string]: any;
}

export interface FieldColumn extends PlanField {
  id: string;
  _aiExtra?: boolean;
  _customField?: boolean;
  _samples?: number;
}

export interface AiFieldMeta {
  shown?: string[];
  hidden?: string[];
}

/** A sample ref without surrounding quotes (as shown). */
export const normRef = (ref: any) => (typeof ref === 'string' ? ref.replace(/^["']+|["']+$/g, '').trim() : ref);

/** The key one sample is matched on: the selection, the evidence map and the results can spell it differently. */
export const sampleKey = (r: any) =>
  String(r ?? '').replace(/^["']+|["']+$/g, '').trim().toLowerCase().replace(/\s+/g, ' ');

// ── similarity (plan field ↔ live label, plan field ↔ AI key) ─────────────────────────────────────────────────

function words(s: string): string[] {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
}

function jaccardScore(a: string[], b: string[]): number {
  if (!a.length && !b.length) return 0;
  const sa = new Set(a);
  const sb = new Set(b);
  let inter = 0;
  sa.forEach((w) => { if (sb.has(w)) inter++; });
  return inter / (sa.size + sb.size - inter);
}

// Character bigrams catch morphological variants (approved/approver, record/recorded).
function bigramScore(strA: string, strB: string): number {
  const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const bigrams = (s: string) => {
    const bg = new Set<string>();
    for (let i = 0; i < s.length - 1; i++) bg.add(s.slice(i, i + 2));
    return bg;
  };
  const a = bigrams(clean(strA));
  const b = bigrams(clean(strB));
  if (!a.size && !b.size) return 0;
  let inter = 0;
  a.forEach((bg) => { if (b.has(bg)) inter++; });
  return inter / (a.size + b.size - inter);
}

/** Max of word-overlap and character-bigram similarity. */
export function similarity(a: string, b: string): number {
  return Math.max(jaccardScore(words(a), words(b)), bigramScore(a, b));
}

/** Threshold for "the same field": catches renames ("Approver Name" ↔ "Approved By", 0.43) without pairing
 *  unrelated ones ("Approval Before Payment" ↔ "Approver Has Payment Authority", 0.35). */
export const FIELD_MATCH_THRESHOLD = 0.38;

/** snake_key → "Snake Key". */
export function toTitle(k: string): string {
  return k.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** A field's value from a sample's extracted values: the label, field_key or live label, exactly or ignoring case.
 *  No fuzzy fallback (a similar-looking key from another field showed the wrong value). Null when not found. */
export function lookupField(fieldData: Record<string, any> | null | undefined, planField: PlanField): any {
  if (!fieldData) return null;
  const label = (planField.label || '').trim();
  const fieldKey = (planField.field_key || '').trim();
  const liveLabel = (planField._liveLabel || '').trim();

  let val = (label && fieldData[label]) || (fieldKey && fieldData[fieldKey]) || (liveLabel && fieldData[liveLabel]) || '';

  if (!val) {
    const targets = [label, fieldKey, fieldKey.replace(/_/g, ' '), liveLabel].filter(Boolean).map((s) => s.toLowerCase());
    for (const k of Object.keys(fieldData)) {
      if (k.startsWith('_')) continue;
      if (targets.includes(k.toLowerCase())) { val = fieldData[k]; break; }
    }
  }
  if (!val || val === 'NOT_FOUND' || val === 'not found' || val === 'not_found') return null;
  return val;
}

// ── the sample pivot ──────────────────────────────────────────────────────────────────────────────────────────

export interface SamplePivot {
  /** [shown ref, row]: row._fieldData = the sample's values; row[attrRef] = that attribute's result cell. */
  rows: Array<[string, any]>;
  aiFieldMeta: AiFieldMeta | null;
  /** field key → { file → Set(pages) }: where each column's values were read. */
  fieldSources: Record<string, Record<string, Set<string>>>;
  /** Findings not about any line item: a plain note for the reviewer. */
  reviewNotes: Array<{ sample: string; attrRef: string; attrLabel: string; note: string }>;
}

/** One row per sample (the expected samples first, in order), with each attribute's result and the values found. */
export function pivotSamples(attributes: any[], expectedSamples: string[] = []): SamplePivot {
  const byRef = new Map<string, any>();
  const ensureRow = (ref: any) => {
    const key = sampleKey(ref);
    if (!byRef.has(key)) byRef.set(key, { _fieldData: {}, _label: normRef(ref) });
    return byRef.get(key);
  };
  expectedSamples.forEach((ref) => ensureRow(ref));

  const reviewNotes: SamplePivot['reviewNotes'] = [];
  let aiFieldMeta: AiFieldMeta | null = null;
  const fieldSources: SamplePivot['fieldSources'] = {};
  attributes.forEach((attr) => {
    (attr.sample_results || []).forEach((sr: any) => {
      const row = ensureRow(sr.record_ref);
      const incoming = sr.attributes || {};
      if (!aiFieldMeta && incoming._ai_fields) {
        try { aiFieldMeta = JSON.parse(incoming._ai_fields); } catch { /* older runs have none */ }
      }
      if (incoming._field_sources) {
        try {
          Object.entries(JSON.parse(incoming._field_sources)).forEach(([key, src]: [string, any]) => {
            if (!src?.file) return;
            const files = (fieldSources[key] = fieldSources[key] || {});
            files[src.file] = files[src.file] || new Set();
            if (src.page) files[src.file].add(String(src.page));
          });
        } catch { /* ignore a malformed entry */ }
      }
      if (incoming._review_note) {
        reviewNotes.push({ sample: sr.record_ref, attrRef: attr.attribute_ref, attrLabel: attr.label || attr.attribute_label, note: incoming._review_note });
      }
      Object.keys(incoming).forEach((k) => {
        if (!k.startsWith('_') && !row._fieldData[k]) row._fieldData[k] = incoming[k];
      });
      row[attr.attribute_ref] = {
        sampleId: sr.id,
        status: sr.status,
        aiResult: sr.ai_result || sr.status,
        humanResult: sr.human_result,
        finalResult: sr.final_result,
        humanRationale: sr.human_rationale,
        observation: sr.observation,
        severity: sr.severity,
        excluded: sr.excluded,
        exclusionReason: sr.exclusion_reason,
        needsAdditionalEvidence: sr.needs_additional_evidence,
        items: sr.item_results || [],
        citation: sr.citation || null,
        citations: sr.citations || [],
        incomplete: incoming._incomplete || null,
      };
    });
  });
  return { rows: [...byRef.values()].map((data) => [data._label, data]), aiFieldMeta, fieldSources, reviewNotes };
}

// ── columns ───────────────────────────────────────────────────────────────────────────────────────────────────

const aiKeysOf = (rows: SamplePivot['rows']) => {
  const keys = new Set<string>();
  rows.forEach(([, data]) => Object.keys(data._fieldData || {}).forEach((k) => { if (!k.startsWith('_')) keys.add(k); }));
  return keys;
};

/** The plan's fields as columns, each with the live plan's label when it was renamed since the run. With no plan
 *  fields, the run's most relevant AI fields (all of them for older runs). */
export function planFieldColumns(planFields: PlanField[], livePlanFields: PlanField[], rows: SamplePivot['rows'],
  aiFieldMeta: AiFieldMeta | null): FieldColumn[] {
  if (planFields.length === 0) {
    const shown = aiFieldMeta?.shown;
    return [...aiKeysOf(rows)].filter((k) => !shown || shown.includes(k)).map((k) => ({ id: k, field_key: k, label: k }));
  }
  const assignedLive = new Set<number>();
  const snapToLive = new Map<number, string>();
  if (livePlanFields.length > 0) {
    const pairs: Array<{ si: number; li: number; score: number; snapLabel: string; liveLabel: string }> = [];
    planFields.forEach((sf, si) => {
      const sfL = sf.label || '';
      const sfFk = sf.field_key || '';
      livePlanFields.forEach((lf, li) => {
        const lfL = lf.label || '';
        const lfFk = lf.field_key || '';
        const score = Math.max(similarity(sfL, lfL), similarity(sfFk, lfFk), similarity(sfL, lfFk), similarity(sfFk, lfL));
        if (score >= FIELD_MATCH_THRESHOLD) pairs.push({ si, li, score, snapLabel: sfL, liveLabel: lfL });
      });
    });
    pairs.sort((a, b) => b.score - a.score);
    pairs.forEach(({ si, li, snapLabel, liveLabel }) => {
      if (assignedLive.has(li)) return;
      assignedLive.add(li);
      const snapNorm = (snapLabel || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const liveNorm = (liveLabel || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      if (liveNorm !== snapNorm) snapToLive.set(si, liveLabel);
    });
  }
  // A field without an id is known by its key or label; a second one with the same key or label gets its position
  // added, so hiding one column does not hide both.
  const used = new Set<string>();
  return planFields.map((pf, i) => {
    let id = pf.id || String(pf.field_key || pf.label || i);
    if (!pf.id && used.has(id)) id = `${id}#${i}`;
    used.add(id);
    return { ...pf, id, _liveLabel: snapToLive.get(i) || null };
  });
}

/** Fields the AI found that no plan field covers, most relevant first. */
export function aiExtraColumns(planFields: PlanField[], planColumns: FieldColumn[], rows: SamplePivot['rows'],
  aiFieldMeta: AiFieldMeta | null): FieldColumn[] {
  if (planFields.length === 0 && !aiFieldMeta) return []; // auto-discovery already made them the plan columns
  const allAiKeys = aiKeysOf(rows);
  const ranked = [...(aiFieldMeta?.shown || []), ...(aiFieldMeta?.hidden || [])];
  const rank = (k: string) => { const i = ranked.indexOf(k); return i === -1 ? ranked.length : i; };
  const toColumn = (k: string): FieldColumn => ({
    id: `_aiex_${k}`, field_key: k, label: toTitle(k), _aiExtra: true,
    _samples: rows.filter(([, d]) => d._fieldData?.[k]).length,
  });

  const covered = new Set<string>();
  if (planFields.length === 0) {
    // Matched exactly: between AI names the fuzzy match hid real fields (invoice_number vs invoice_date).
    planColumns.forEach((c) => covered.add(String(c.field_key)));
    return [...allAiKeys].filter((k) => !covered.has(k)).sort((a, b) => rank(a) - rank(b)).map(toColumn);
  }
  planColumns.forEach((ef) => {
    const efL = (ef.label || '').toLowerCase();
    const efK = (ef.field_key || '').toLowerCase();
    allAiKeys.forEach((k) => {
      const kLo = k.toLowerCase();
      if (kLo === efL || kLo === efK || kLo === efK.replace(/_/g, ' ')) { covered.add(k); return; }
      const score = Math.max(similarity(ef.label || '', k), similarity(ef.field_key || '', k));
      if (score >= FIELD_MATCH_THRESHOLD) covered.add(k);
    });
  });
  return [...allAiKeys].filter((k) => !covered.has(k)).sort((a, b) => rank(a) - rank(b)).map(toColumn);
}

// ── the reviewer's column choices (this browser) ──────────────────────────────────────────────────────────────

export interface ColumnChoices {
  hidden: string[];  // column ids the reviewer hid
  shown: string[];   // AI-found columns the reviewer added from Columns
  custom: string[];  // field keys the reviewer typed in
}

export const columnStorageKeys = (runId: string) => ({
  hidden: `sf_hidden_${runId}`, shown: `sf_shown_${runId}`, custom: `sf_custom_${runId}`,
});

function readList(key: string | null): string[] {
  if (!key) return [];
  try {
    const v = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

/** The column choices saved in this browser for a run (empty when none, or storage is unavailable). */
export function loadColumnChoices(runId?: string | null): ColumnChoices {
  if (!runId) return { hidden: [], shown: [], custom: [] };
  const keys = columnStorageKeys(runId);
  return { hidden: readList(keys.hidden), shown: readList(keys.shown), custom: readList(keys.custom) };
}

export function saveColumnChoice(runId: string | null | undefined, which: keyof ColumnChoices, ids: string[]): void {
  if (!runId) return;
  try { window.localStorage.setItem(columnStorageKeys(runId)[which], JSON.stringify(ids)); } catch { /* not remembered */ }
}

export interface ColumnSet {
  allFields: FieldColumn[];
  visibleFields: FieldColumn[];
  hiddenFields: FieldColumn[];   // hidden plan / custom columns (restorable)
  hiddenExtras: FieldColumn[];   // AI-found columns not shown
  effectiveHiddenIds: string[];
}

/** Which columns are shown: plan fields and the AI fields the run marked very relevant, plus what the reviewer
 *  added; minus what they hid. */
export function resolveColumns(planColumns: FieldColumn[], extraColumns: FieldColumn[], choices: ColumnChoices,
  aiFieldMeta: AiFieldMeta | null): ColumnSet {
  const relevantShown = new Set(aiFieldMeta?.shown || []);
  const defaultHiddenIds = extraColumns
    .filter((f) => !relevantShown.has(String(f.field_key)) && !choices.shown.includes(f.id))
    .map((f) => f.id);
  const effectiveHiddenIds = [...new Set([...choices.hidden, ...defaultHiddenIds])];
  const customColumns: FieldColumn[] = choices.custom.map((k) => ({ id: `_custom_${k}`, field_key: k, label: toTitle(k), _customField: true }));
  const allFields = [...planColumns, ...extraColumns, ...customColumns];
  return {
    allFields,
    visibleFields: allFields.filter((f) => !effectiveHiddenIds.includes(f.id)),
    hiddenFields: allFields.filter((f) => effectiveHiddenIds.includes(f.id) && !f._aiExtra),
    hiddenExtras: extraColumns.filter((f) => effectiveHiddenIds.includes(f.id)),
    effectiveHiddenIds,
  };
}

/** Where a column's values were read: "Source: file (p. 1, 6); other.pdf". '' when not recorded. */
export function sourceText(fieldSources: Record<string, Record<string, Set<string> | string[]>> | null | undefined,
  field: PlanField): string {
  const files = fieldSources?.[String(field.field_key)] || fieldSources?.[String(field.label)];
  if (!files) return '';
  const parts = Object.entries(files).map(([file, pages]) => {
    const list = [...pages].sort((a, b) => Number(a) - Number(b));
    return list.length ? `${file} (p. ${list.join(', ')})` : file;
  });
  return parts.length ? `Source: ${parts.join('; ')}` : '';
}

/** Line-item column visibility saved in this browser: column id → shown. Columns not in it use their default. */
export const lineColumnsStorageKey = (runId: string) => `li_cols_${runId}`;

export function loadLineColumnChoices(runId?: string | null): Record<string, boolean> {
  if (!runId) return {};
  try {
    const v = JSON.parse(window.localStorage.getItem(lineColumnsStorageKey(runId)) || '{}');
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

export const lineColumnVisible = (choices: Record<string, boolean>, col: { id: string; defaultVisible?: boolean }) =>
  (col.id in choices ? choices[col.id] : !!col.defaultVisible);
